/**
 * ThryvQualified — bootstrap entry point.
 *
 * Rollup entry for the ThryvQualified bundle (rollup.config.mjs →
 * createJsConfigs). Output lands at dist-js/ThryvQualified.min.js,
 * enqueued globally via the theme's asset registration pipeline.
 *
 * Sub-namespaces:
 *   ThryvQualified.Commands  — qualified() command wrappers
 *   ThryvQualified.Events    — handleEvents listener + DOM dispatch + analytics
 *   ThryvQualified.Helpers   — shared utility functions
 *   ThryvQualified.AskQuinn  — DOM-level controls for the Ask Quinn dock + drawer
 *
 * Top-level methods:
 *   ThryvQualified.init()              — runs on DOMReady
 *   ThryvQualified.identifyVisitor()   — pushes qualified_chat_status to Qualified
 *   ThryvQualified.onAskQuinnReady(fn)     — fire fn when Ask Quinn mounts
 *   ThryvQualified.hideAskQuinn()      — shortcut to AskQuinn.hide()
 *   ThryvQualified.showAskQuinn()      — shortcut to AskQuinn.show()
 *   ThryvQualified.toggleAskQuinn()    — shortcut to AskQuinn.toggle()
 *
 * The qualified() stub queue is installed by the GTM Qualified tag — we
 * don't install our own.
 *
 * Usage:
 *   ThryvQualified.Commands.identify({ email: 'x@y.com' });
 *
 *   ThryvQualified.AskQuinn.hide();         // hide drawer + launcher
 *   ThryvQualified.AskQuinn.show();         // bring them back
 *   ThryvQualified.AskQuinn.toggle();       // flip current state
 *   ThryvQualified.AskQuinn.isHidden();     // boolean
 *
 *   ThryvQualified.AskQuinn.hideWhileLoading();   // suppress during init
 *   ThryvQualified.AskQuinn.showAfterLoading();   // reveal after init
 *   ThryvQualified.AskQuinn.onLoaded(fn);         // fn fires when ready
 *   ThryvQualified.AskQuinn.watchStateChanges();  // emit open/close events
 *   ThryvQualified.AskQuinn.getStoredState();     // { docked, raw } | null
 *
 *   // Or via top-level shortcuts
 *   ThryvQualified.hideAskQuinn();
 *   ThryvQualified.showAskQuinn();
 *   ThryvQualified.toggleAskQuinn();
 *
 *   ThryvQualified.onAskQuinnReady(() => ThryvQualified.hideAskQuinn());
 *
 *   // DOM events:
 *   //   qualified.ask-quinn-ready    — UI container mounted
 *   //   qualified.ask-quinn-loaded   — loading skeleton gone, drawer ready
 *   //   qualified.ask-quinn-opened   — drawer opened (q-docked=true)
 *   //   qualified.ask-quinn-closed   — drawer closed (q-docked removed)
 *   document.addEventListener('qualified.ask-quinn-loaded', () => { ... });
 */
import Commands from "./Commands.js";
import Events from "./Events.js";
import Helpers from "./Helpers.js";
import AskQuinn from "./AskQuinn.js";

window.ThryvQualified = window.ThryvQualified || {};

// Attach sub-namespaces
window.ThryvQualified.Commands = Commands;
window.ThryvQualified.Events = Events;
window.ThryvQualified.Helpers = Helpers;
window.ThryvQualified.AskQuinn = AskQuinn;

// Top-level shortcuts for the Ask Quinn experience (Qualified's docked
// launcher + drawer). Schedule-modal and classic chat are separate
// experiences and aren't affected by these.
window.ThryvQualified.hideAskQuinn = function () {
  AskQuinn.hide();
};
window.ThryvQualified.showAskQuinn = function () {
  AskQuinn.show();
};
window.ThryvQualified.toggleAskQuinn = function () {
  AskQuinn.toggle();
};

/**
 * Push the page-level `qualified_chat_status` flag to Qualified as a
 * visitor attribute. The chat experience's audience filter on Tom's side
 * targets `qualified_chat_status === true`, so chat only deploys on
 * opted-in pages. Schedule-demo modal is a separate experience and is
 * unaffected.
 *
 * Waits for window.qualified to be callable before firing — the GTM tag
 * installs the stub on DOM Ready, so timing relative to our DOMReady
 * bootstrap can race.
 */
window.ThryvQualified.identifyVisitor = function () {
  Helpers.waitForSdk(function () {
    Commands.identify({
      qualified_chat_status: window.thryvData && window.thryvData.qualified_chat_status === true,
    });
  });
};

/**
 * Fire `callback` once Qualified has mounted its UI container
 * (#qualified-multimodal-host appears in the DOM). Also dispatches a
 * `qualified.ask-quinn-ready` CustomEvent on document so multiple listeners
 * can react in parallel.
 *
 * One-shot: callback fires once, then the observer disconnects. If
 * Qualified already mounted before this is called, callback runs
 * synchronously.
 *
 * Usage:
 *   ThryvQualified.onAskQuinnReady(() => ThryvQualified.hideAskQuinn());
 *
 *   // Or via DOM event:
 *   document.addEventListener('qualified.ask-quinn-ready', () => {
 *     ThryvQualified.hideAskQuinn();
 *   });
 */
window.ThryvQualified.onAskQuinnReady = function (callback) {
  const fire = function () {
    document.dispatchEvent(new CustomEvent("qualified.ask-quinn-ready"));
    if (typeof callback === "function") callback();
  };

  // Already mounted? Fire immediately.
  if (document.getElementById("qualified-multimodal-host")) {
    fire();
    return;
  }

  // Otherwise watch for it. Body might not be ready yet if called very early —
  // fall back to documentElement.
  if (!window.MutationObserver) return;

  const root = document.body || document.documentElement;
  const observer = new MutationObserver(function () {
    if (document.getElementById("qualified-multimodal-host")) {
      observer.disconnect();
      fire();
    }
  });

  observer.observe(root, { childList: true, subtree: true });
};

// Bootstrap method — runs on DOM ready
window.ThryvQualified.init = function () {
  this.Events.init();
  this.identifyVisitor();

  // Always watch open/close transitions so listeners can react regardless of
  // whether chat is enabled on this page.
  this.AskQuinn.watchStateChanges();

  const chatEnabled = window.thryvData && window.thryvData.qualified_chat_status === true;

  // The server emits a render-blocking <style id="thryv-ask-quinn-loading">
  // in <head> (see app/Hooks/AskQuinnInitialHide.php), so the drawer is
  // hidden from frame 1. JS only decides whether to keep it hidden
  // permanently or reveal it after loading.

  // Chat-disabled pages: replace the loading-style with the permanent
  // suppression style. Catches false, undefined, and missing thryvData
  // (fail-closed).
  if (!chatEnabled) {
    this.onAskQuinnReady(function () {
      window.ThryvQualified.hideAskQuinn();
      window.ThryvQualified.AskQuinn.showAfterLoading();
    });
    return;
  }

  // Chat-enabled pages: keep the server-side style in place, then remove it
  // once Ask Quinn has settled. hideWhileLoading() is a no-op when the
  // server-side style is already present, but acts as a defensive fallback
  // if it isn't (e.g. a header template that skips right_after_head_tag).
  this.AskQuinn.hideWhileLoading();
  this.onAskQuinnReady(function () {
    window.ThryvQualified.AskQuinn.onLoaded(function () {
      window.ThryvQualified.AskQuinn.showAfterLoading();
    });
  });
};

DOMReady(function () {
  window.ThryvQualified.init();
});
